import React, { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  EyeIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import api from '../../services/api'

interface SubscriptionRow {
  id: number
  plan_name: string | null
  plan_type: string | null
  status: string
  start_date: string | null
  end_date: string | null
  amount_paid: number | null
  order_id: string | null
  payment_id: string | null
}

interface Client {
  id: number
  full_name: string
  email: string
  phone?: string
  pan_number?: string
  date_of_birth?: string
  kyc_status: string
  onboarding_step: string
  is_active: boolean
  created_at: string
  last_login?: string
  kyc?: {
    status: string
    esign_status: string
    cams_kyc_verified?: string
    submitted_at?: string
    pan_number?: string
    date_of_birth?: string
    gender?: string
    father_name?: string
    address_line1?: string
    address_line2?: string
    city?: string
    state?: string
    pincode?: string
    aadhar_number?: string
  } | null
  subscription?: { plan_name: string; status: string; end_date: string } | null
  subscriptions?: SubscriptionRow[]
}

const StepBadge: React.FC<{ step: string }> = ({ step }) => {
  const map: Record<string, [string, string]> = {
    complete: ['bg-green-500/10 text-green-400 border-green-500/20', 'Complete'],
    // Payment is no longer an onboarding step — eSign completion marks the flow
    // complete. Legacy accounts left at "payment" are shown as Complete.
    payment:  ['bg-green-500/10 text-green-400 border-green-500/20', 'Complete'],
    esign:    ['bg-violet-500/10 text-violet-400 border-violet-500/20', 'eSign'],
    kyc:      ['bg-orange-500/10 text-orange-400 border-orange-500/20', 'KYC'],
    register: ['bg-dark-700 text-gray-400 border-dark-600', 'Registered'],
  }
  const [cls, label] = map[step] || ['bg-dark-700 text-gray-400 border-dark-600', step]
  return <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${cls}`}>{label}</span>
}

const KYCBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, [string, string]> = {
    approved:     ['bg-green-500/10 text-green-400', 'Approved'],
    under_review: ['bg-yellow-500/10 text-yellow-400', 'Under Review'],
    pending:      ['bg-orange-500/10 text-orange-400', 'Pending'],
    rejected:     ['bg-red-500/10 text-red-400', 'Rejected'],
  }
  const [cls, label] = map[status] || ['bg-dark-700 text-gray-400', status]
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>
}

const SubStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, [string, string]> = {
    active:    ['bg-green-500/10 text-green-400 border-green-500/20', 'Active'],
    expired:   ['bg-dark-700 text-gray-400 border-dark-600', 'Expired'],
    cancelled: ['bg-red-500/10 text-red-400 border-red-500/20', 'Cancelled'],
    suspended: ['bg-orange-500/10 text-orange-400 border-orange-500/20', 'Suspended'],
  }
  const [cls, label] = map[status] || ['bg-dark-700 text-gray-400 border-dark-600', status]
  return <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${cls}`}>{label}</span>
}

function PlanRow({ sub }: { sub: SubscriptionRow }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="rounded-xl border border-dark-600 bg-dark-700/40 overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-dark-700/70 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-white text-sm truncate">{sub.plan_name || 'Unknown plan'}</span>
          {sub.plan_type && <span className="text-xs text-gray-500 flex-shrink-0">({sub.plan_type})</span>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
          <span className="text-xs text-gray-300">₹{sub.amount_paid != null ? sub.amount_paid.toLocaleString('en-IN') : '—'}</span>
          <span className="text-xs text-gray-500">till {sub.end_date ? new Date(sub.end_date).toLocaleDateString('en-IN') : '—'}</span>
          <SubStatusBadge status={sub.status} />
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-dark-600 grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
          <Row label="Start Date" value={sub.start_date ? new Date(sub.start_date).toLocaleDateString('en-IN') : '—'} />
          <Row label="End Date" value={sub.end_date ? new Date(sub.end_date).toLocaleDateString('en-IN') : '—'} />
          <Row label="Order ID" value={<span className="font-mono text-[11px]">{sub.order_id || '—'}</span>} />
          <Row label="Transaction ID" value={<span className="font-mono text-[11px]">{sub.payment_id || '—'}</span>} />
        </div>
      )}
    </div>
  )
}

interface KYCModalProps {
  client: Client
  onClose: () => void
  onAction: () => void
}

function KYCModal({ client, onClose, onAction }: KYCModalProps) {
  const [action, setAction] = useState<'approve' | 'reject' | ''>('')
  const [remarks, setRemarks] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'personal' | 'plans'>('personal')
  const [kycStatus, setKycStatus] = useState(client.kyc_status)

  useEffect(() => {
    setKycStatus(client.kyc_status)
  }, [client.kyc_status])


  const submit = async () => {
    if (!action) return
    setLoading(true)
    setError('')
    try {
      const res = await api.put(`/admin/clients/${client.id}/kyc`, { action, remarks })
      if (res.data && res.data.kyc_status) {
        setKycStatus(res.data.kyc_status)
      }
      onAction()
      onClose()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Action failed')
    } finally {
      setLoading(false)
    }
  }

  const dob = client.kyc?.date_of_birth || client.date_of_birth
  const subscriptions = client.subscriptions || []
  const activeCount = subscriptions.filter(s => s.status === 'active').length
  // Active plans first, expired/cancelled/suspended pushed to the bottom
  const sortedSubscriptions = [...subscriptions].sort((a, b) =>
    (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1)
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6 overflow-y-auto">
      <div className="bg-dark-800 rounded-2xl shadow-xl w-full max-w-2xl p-6 border border-dark-700 my-auto">
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="font-bold text-white text-xl">Client Details</h3>
            <p className="text-sm text-gray-400 mt-0.5">{client.full_name} · {client.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-dark-600 mb-5">
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'personal' ? 'border-primary-500 text-primary-400' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
            onClick={() => setActiveTab('personal')}
          >
            Personal & KYC
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-1.5 ${activeTab === 'plans' ? 'border-primary-500 text-primary-400' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
            onClick={() => setActiveTab('plans')}
          >
            Plans
            {activeCount > 0 && (
              <span className="text-[10px] font-bold bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">{activeCount}</span>
            )}
          </button>
        </div>

        <div className="min-h-[250px] mb-5">
          {activeTab === 'personal' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <Row label="Phone" value={client.phone || '—'} />
                <Row label="DOB" value={dob ? new Date(dob).toLocaleDateString('en-IN') : '—'} />
                <Row label="Onboarding Step" value={<StepBadge step={client.onboarding_step} />} />
                <Row label="Joined" value={new Date(client.created_at).toLocaleDateString('en-IN')} />
                <Row label="eSign Status" value={client.kyc?.esign_status || '—'} />
                <Row label="CAMS KYC" value={client.kyc?.cams_kyc_verified || '—'} />
                <Row label="KYC Status" value={<KYCBadge status={client.kyc_status} />} />
                <Row label="Last Login" value={client.last_login ? new Date(client.last_login).toLocaleString('en-IN') : '—'} />
              </div>

              <div className="grid grid-cols-2 gap-x-8 gap-y-3 pt-3 border-t border-dark-700">
                <Row label="PAN Number" value={<span className="font-mono">{client.pan_number || client.kyc?.pan_number || '—'}</span>} />
                <Row label="Aadhar Number" value={<span className="font-mono">{client.kyc?.aadhar_number || '—'}</span>} />
                <Row label="Father's Name" value={client.kyc?.father_name || '—'} />
                <Row label="Gender" value={client.kyc?.gender || '—'} />
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2 pt-2 border-t border-dark-700">Address Information</h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  <Row label="Address Line 1" value={client.kyc?.address_line1 || '—'} />
                  <Row label="Address Line 2" value={client.kyc?.address_line2 || '—'} />
                  <Row label="City" value={client.kyc?.city || '—'} />
                  <Row label="State" value={client.kyc?.state || '—'} />
                  <Row label="Pincode" value={client.kyc?.pincode || '—'} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'plans' && (
            <div className="space-y-2">
              {sortedSubscriptions.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-12">No subscriptions yet</p>
              ) : (
                sortedSubscriptions.map(sub => <PlanRow key={sub.id} sub={sub} />)
              )}
            </div>
          )}
        </div>

        {kycStatus !== 'approved' && (
          <div className="border-t border-dark-700 pt-4 mt-2">
            <h4 className="text-sm font-medium text-white mb-3">KYC Actions</h4>
            {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Remarks (optional)</label>
              <textarea
                rows={2}
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
                className="w-full text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
                placeholder="Internal remarks…"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => { setAction('approve'); setTimeout(submit, 0) }}
                disabled={loading}
                className="flex-1 py-2.5 rounded-xl bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {loading && action === 'approve' ? 'Approving…' : 'Approve KYC'}
              </button>
              <button
                onClick={() => { setAction('reject'); setTimeout(submit, 0) }}
                disabled={loading}
                className="flex-1 py-2.5 rounded-xl bg-red-500/10 text-red-400 text-sm font-medium hover:bg-red-500/20 disabled:opacity-50 transition-colors"
              >
                {loading && action === 'reject' ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-100 text-right">{value}</span>
    </div>
  )
}

const PAGE_SIZE = 20

export default function ClientRecords() {
  const [clients, setClients] = useState<Client[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [kycFilter, setKycFilter] = useState('')
  const [stepFilter, setStepFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Client | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE }
      if (search) params.search = search
      if (kycFilter) params.kyc_status = kycFilter
      if (stepFilter) params.onboarding_step = stepFilter
      if (activeFilter) params.is_active = activeFilter === 'active'
      if (planFilter) params.has_active_plan = planFilter === 'yes'
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo
      const res = await api.get('/admin/clients', { params })
      setClients(res.data.clients)
      setTotal(res.data.total)
    } catch {}
    finally { setLoading(false) }
  }, [search, kycFilter, stepFilter, activeFilter, planFilter, dateFrom, dateTo, page])

  useEffect(() => { fetch() }, [fetch])
  // Reset to page 1 whenever a filter (not the page itself) changes
  useEffect(() => { setPage(1) }, [search, kycFilter, stepFilter, activeFilter, planFilter, dateFrom, dateTo])

  const toggleActive = async (client: Client) => {
    try {
      await api.put(`/admin/clients/${client.id}/status`, { is_active: !client.is_active })
      fetch()
    } catch {}
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const activeExtraFilters = (activeFilter ? 1 : 0) + (planFilter ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <nav className="text-xs text-gray-400 mb-1"><Link to="/admin" className="hover:text-amber-400 transition-colors">RA Desk</Link> &rsaquo; Client Records</nav>
          <h1 className="text-2xl font-bold text-white">Client Records</h1>
          <p className="text-sm text-gray-400 mt-0.5">{total} clients total</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ width: 16, height: 16 }} />
          <input
            type="text"
            placeholder="Search name, email, PAN…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={kycFilter}
            onChange={e => setKycFilter(e.target.value)}
            className="text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All KYC</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={stepFilter}
            onChange={e => setStepFilter(e.target.value)}
            className="text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Steps</option>
            <option value="register">Registered</option>
            <option value="kyc">KYC</option>
            <option value="esign">eSign</option>
            <option value="complete">Complete</option>
          </select>

          <div className="relative">
            <button
              onClick={() => setShowMoreFilters(s => !s)}
              className="inline-flex items-center gap-1.5 text-sm bg-dark-900 border border-dark-600 text-gray-300 rounded-lg px-3 py-2 hover:bg-dark-700 transition-colors relative"
            >
              <FunnelIcon style={{ width: 15, height: 15 }} />
              More Filters
              {activeExtraFilters > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary-500 text-white text-[10px] flex items-center justify-center font-bold">
                  {activeExtraFilters}
                </span>
              )}
            </button>
            {showMoreFilters && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-dark-800 border border-dark-600 rounded-xl shadow-xl z-20 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-white">More Filters</p>
                  <button onClick={() => setShowMoreFilters(false)} className="text-gray-400 hover:text-white">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1.5 block">Account Status</label>
                  <select
                    value={activeFilter}
                    onChange={e => setActiveFilter(e.target.value)}
                    className="w-full text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1.5 block">Active Plan</label>
                  <select
                    value={planFilter}
                    onChange={e => setPlanFilter(e.target.value)}
                    className="w-full text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">All</option>
                    <option value="yes">Has active plan</option>
                    <option value="no">No active plan</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1.5 block">Joined Between</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={e => setDateFrom(e.target.value)}
                      className="text-xs bg-dark-900 border border-dark-600 rounded-lg px-2 py-1.5 text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                    <input
                      type="date"
                      value={dateTo}
                      onChange={e => setDateTo(e.target.value)}
                      className="text-xs bg-dark-900 border border-dark-600 rounded-lg px-2 py-1.5 text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <button
                  onClick={() => { setActiveFilter(''); setPlanFilter(''); setDateFrom(''); setDateTo('') }}
                  className="w-full text-xs px-3 py-2 rounded-lg border border-dark-600 text-gray-300 hover:bg-dark-700 transition-colors"
                >
                  Clear extra filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700 bg-dark-900">
                {['Client', 'PAN', 'KYC Status', 'Onboarding', 'Plan', 'Joined', 'Active', 'Actions'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading…</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">No clients found</td></tr>
              ) : clients.map(c => (
                <tr key={c.id} className="border-b border-dark-700 hover:bg-dark-700 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-white">{c.full_name}</p>
                    <p className="text-xs text-gray-400">{c.email}</p>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-400">{c.pan_number || '—'}</td>
                  <td className="px-4 py-3"><KYCBadge status={c.kyc_status} /></td>
                  <td className="px-4 py-3"><StepBadge step={c.onboarding_step} /></td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {c.subscription ? (
                      <span>
                        {c.subscription.plan_name}
                        <br />
                        <span className="text-gray-400">till {new Date(c.subscription.end_date).toLocaleDateString('en-IN')}</span>
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(c)}
                      className={`w-8 h-4.5 rounded-full transition-colors relative ${c.is_active ? 'bg-green-500' : 'bg-dark-600'}`}
                      style={{ height: 18, width: 32 }}
                    >
                      <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-all ${c.is_active ? 'left-4' : 'left-0.5'}`} style={{ width: 14, height: 14, top: 2, left: c.is_active ? 16 : 2 }} />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelected(c)}
                      className="p-1.5 text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
                      title="Review KYC"
                    >
                      <EyeIcon style={{ width: 16, height: 16 }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && clients.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-dark-700">
            <p className="text-xs text-gray-400">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors"
              >
                <ChevronLeftIcon style={{ width: 15, height: 15 }} />
              </button>
              <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors"
              >
                <ChevronRightIcon style={{ width: 15, height: 15 }} />
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <KYCModal client={selected} onClose={() => setSelected(null)} onAction={fetch} />
      )}
    </div>
  )
}
